<script setup lang="ts">
import { computed } from 'vue'
import { Maximize2, Minimize2 } from '@lucide/vue'

import type { WidgetWrapperMetric } from './types'
import { useFullscreen } from './useFullscreen'
import ActionButton from '@/views/Dashboards/Dashboard/components/ActionButton.vue'

type Props = {
  sectionLabel?: string
  title?: string
  metric?: WidgetWrapperMetric[]
}

const { sectionLabel = '', title = '', metric } = defineProps<Props>()

defineOptions({ inheritAttrs: false })

const isSectionLabelVisible = computed(() => title !== sectionLabel && title !== '')

const { isFullscreen, toggle: toggleFullscreen, exit: exitFullscreen } = useFullscreen()
</script>

<template>
  <div
    v-if="isFullscreen"
    class="fixed inset-0 z-50 bg-scrim"
    @click="exitFullscreen"
  />
  <div
    v-bind="$attrs"
    class="flex flex-col bg-card border border-border rounded-ds-lg"
    :class="isFullscreen ? 'fixed inset-3 z-50 shadow-ds-xl' : 'w-full h-full shadow-ds-md'"
  >
    <div
      class="min-h-18.25 flex gap-3 px-6 py-5 border-b border-border"
      :class="isSectionLabelVisible ? 'items-start' : 'items-center'"
    >
      <div class="flex flex-col min-w-0 gap-0.5">
        <div
          v-if="isSectionLabelVisible"
          class="text-[11px] leading-none font-bold uppercase tracking-ds-caps text-muted-foreground/75 truncate"
        >
          {{ sectionLabel }}
        </div>
        <div
          class="-mb-1 text-lg font-bold leading-tight tracking-ds-tight truncate"
          :class="!title ? 'text-muted-foreground/75' : 'text-foreground'"
        >
          {{ title || sectionLabel }}
        </div>
      </div>
      <slot name="header" />
      <div class="flex items-center gap-2 flex-none ml-auto">
        <div
          v-for="(item, index) in metric"
          :key="index"
          v-tooltip.top="item.tooltip"
          class="flex items-center gap-1.5 py-1 px-3 bg-accent rounded-full whitespace-nowrap"
        >
          <component
            :is="item.icon"
            v-if="item.icon"
            class="size-3 text-accent-foreground"
          />
          <span
            v-if="item.label"
            class="text-xs font-medium text-accent-foreground"
          >
            {{ item.label }}
          </span>
          <span
            v-if="item.value"
            class="text-[0.8125rem] font-bold tracking-ds-tight text-primary-pressed tabular-nums"
          >
            {{ item.value }}
          </span>
        </div>
        <!--
          Real widgets range from "nothing" to a stateful search/filter/sort/
          export bar, and every caller has an explicit opinion — no consumer
          wants a decorative default, so these are bare outlets with no
          fallback content.
        -->
        <slot name="actions" />
        <slot name="protect" />
        <ActionButton
          :title="isFullscreen ? 'Exit fullscreen' : 'Fullscreen'"
          :aria-label="isFullscreen ? 'Exit fullscreen' : 'Fullscreen'"
          @click="toggleFullscreen"
        >
          <component
            :is="isFullscreen ? Minimize2 : Maximize2"
          />
        </ActionButton>
      </div>
    </div>
    <div class="px-6 py-5 flex-1 min-h-0 flex flex-col rounded-b-ds-lg overflow-hidden">
      <slot>
        <div class="w-full h-full min-h-70 flex items-center justify-center border border-dashed border-input rounded-ds-md bg-background text-sm font-medium text-muted-foreground">
          Widget content area
        </div>
      </slot>
    </div>
  </div>
</template>
